import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ViewIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import { Box, Button, Typography } from "@mui/material";
import {
  GridActionsCellItem,
  GridColDef,
  GridFilterModel,
  GridSortModel,
} from "@mui/x-data-grid";
import { useState } from "react";
import useModal from "../../hooks/useModal";
import { PaginationModel } from "../../interfaces/pagination";
import { User } from "../../interfaces/user";
import Table, { Text, ToolbarWithAddButton } from "../Table";
import CreateUserDialog from "./CreateUserDialog";
import DeleteUserDialog from "./DeleteUserDialog";
import EditUserDialog from "./EditUserDialog";
import ViewUserDialog from "./ViewUserDialog";
import { formatDate, formatDateTime, fromNow } from "../../utils/date.format.utils";
import AddUserButton from './addUserButton';

const UsersTable = ({
  users,
  rowCount,
  paginationModel,
  setPaginationModel,
  handleFilterModelChange,
  handleSortModelChange,
  isLoading,
  mutate,
}: {
  users: User[];
  rowCount: number;
  paginationModel: PaginationModel;
  setPaginationModel: React.Dispatch<React.SetStateAction<PaginationModel>>;
  handleFilterModelChange: (filterModel: GridFilterModel) => void;
  handleSortModelChange: (sortModel: GridSortModel) => void;
  isLoading: boolean;
  mutate: () => void;
}) => {
  const { open, handleOpen, handleClose } = useModal();
  const {
    open: viewOpen,
    handleOpen: handleViewOpen,
    handleClose: handleViewClose,
  } = useModal();
  const {
    open: editOpen,
    handleOpen: handleEditOpen,
    handleClose: handleEditClose,
  } = useModal();
  const {
    open: deleteOpen,
    handleOpen: handleDeleteOpen,
    handleClose: handleDeleteClose,
  } = useModal();

  const [selectedUser, setSelectedUser] = useState<User>();

  const columns: GridColDef<User>[] = [
    {
      field: "index",
      headerName: "No.",
      sortable: false,
      disableColumnMenu: true,
      width: 45,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => <Text text={`${params.value}.`} />,
    },
    {
      field: "name",
      headerName: "Name",
      minWidth: 150,
      flex: 1,
      renderCell: (params) => <Text text={params.value || "-"} />,
    },
    {
      field: "email",
      headerName: "Email",
      minWidth: 150,
      flex: 1,
      renderCell: (params) => <Text text={params.value || "-"} />,
    },
    {
      field: "created_at",
      headerName: "Date Created",
      minWidth: 150,
      flex: 1,
      valueFormatter: (params) => formatDateTime(params.value),
      renderCell: (params) => (
        <Typography>{formatDateTime(params.value)}</Typography>
      ),
    },
    {
      field: "actions",
      type: "actions",
      width: 36,
      getActions: (params) => {
        return [
          <GridActionsCellItem
            key={"view"}
            icon={<ViewIcon color="primary" fontSize="small" />}
            label="View"
            showInMenu
            onClick={() => {
              setSelectedUser(params.row);
              handleViewOpen();
            }}
          />,
          <GridActionsCellItem
            key={"edit"}
            icon={<EditIcon color="primary" fontSize="small" />}
            label="Edit"
            showInMenu
            onClick={() => {
              setSelectedUser(params.row);
              handleEditOpen();
            }}
          />,
          <GridActionsCellItem
            key={"delete"}
            icon={<DeleteIcon color="error" fontSize="small" />}
            label="Delete"
            showInMenu
            onClick={() => {
              setSelectedUser(params.row);
              handleDeleteOpen();
            }}
          />,
        ];
      },
    },
  ];

  return (
    <>
      <CreateUserDialog open={open} handleClose={handleClose} mutate={mutate} />
      {selectedUser && (
        <ViewUserDialog
          user={selectedUser}
          open={viewOpen}
          handleClose={handleViewClose}
        />
      )}
      {selectedUser && (
        <EditUserDialog
          user={selectedUser}
          open={editOpen}
          handleClose={handleEditClose}
          mutate={mutate}
        />
      )}
      {selectedUser && (
        <DeleteUserDialog
          user={selectedUser}
          open={deleteOpen}
          handleClose={handleDeleteClose}
          mutate={mutate}
        />
      )}
      <Table
        rows={users.map((user, index) => ({
          ...user,
          index: paginationModel.page * paginationModel.pageSize + (index + 1),
        }))}
        columns={columns}
        loading={isLoading}
        sx={{
          display: "grid",
          ".css-axafay-MuiDataGrid-virtualScroller": {
            overflowX: "auto",
          },
        }}
        autoHeight={false}
        rowCount={rowCount}
        pageSizeOptions={[25, 50, 100]}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        filterMode="server"
        onFilterModelChange={handleFilterModelChange}
        sortingMode="server"
        onSortModelChange={handleSortModelChange}
        slots={{ toolbar: ToolbarWithAddButton}}
        slotProps={{
          toolbar: {
            children: <AddUserButton onClick={handleOpen} />,
          },
        }}
      />
    </>
  );
};

export default UsersTable;
